import useNavigate from '@/hooks/useNavigate';
import { ROUTES } from '@/types/common';
import { NATIVE_POST_MESSAGE_TYPE, isWebView, sendPostMessageToNative } from '@/utils/mobile/RNPlugins';
import { useRouter } from 'next/router';
import { MagazineAuthorModel, MagazineByAuthorPublicIdVo } from '../model/magazine';
import useGetMagazineAuthor from './useGetMagazineAuthor';
import useGetMagazineByAuthorPublicId from './useGetMagazineByAuthorPublicId';

export interface UseMagazineAuthorDetailViewModel {
  id: string;
  currentPage: number;
  queries: {
    page: string;
  };
  paginationBaseUrl: string;
  magazineAuthor?: MagazineAuthorModel | null;
  magazineByAuthorPublicId?: MagazineByAuthorPublicIdVo | null;
  openSocialMediaLink: (type: 'instagram' | 'youtube' | 'thread' | 'naverBlog') => void;
  moveToAuthorHospital: () => void;
}

const useMagazineAuthorDetailViewModel = (): UseMagazineAuthorDetailViewModel => {
  const router = useRouter();
  const { id, ...queries } = router.isReady ? (router.query as { id: string; page: string }) : { id: '', page: '1' };
  const currentPage = Number(queries.page) || 1;
  const navigate = useNavigate();

  const { magazineAuthor } = useGetMagazineAuthor({
    authorPublicId: id,
  });

  const { magazineByAuthorPublicId } = useGetMagazineByAuthorPublicId({
    authorPublicId: id,
    page: currentPage,
  });

  const openOutLink = (url: string) => {
    if (isWebView()) {
      sendPostMessageToNative(NATIVE_POST_MESSAGE_TYPE.OPEN_EXTERNAL_BROWSER, {
        url,
      });
    } else {
      window.open(url, '_blank');
    }
  };
  const openSocialMediaLink = (type: 'instagram' | 'youtube' | 'thread' | 'naverBlog') => {
    switch (type) {
      case 'instagram':
        openOutLink('https://www.instagram.com/doctornow_official');
        break;
      case 'youtube':
        openOutLink('https://www.youtube.com/@doctornow_official');
        break;
      case 'thread':
        openOutLink('https://www.threads.com/@doctornow_official');
        break;
      case 'naverBlog':
        openOutLink('https://blog.naver.com/doctornow_official');
        break;
    }
  };

  const moveToAuthorHospital = () => {
    if (!magazineAuthor) {
      return;
    }
    if (!magazineAuthor.affiliation) {
      return;
    }

    navigate.navigateToHospitalDetail({ hospitalPid: magazineAuthor.affiliation.affiliationPid });
  };

  const paginationBaseUrl = `${ROUTES.AUTHOR.DETAIL.path}/${id}`;
  return {
    id,
    currentPage,
    queries,
    paginationBaseUrl,
    magazineAuthor,
    magazineByAuthorPublicId,
    openSocialMediaLink,
    moveToAuthorHospital,
  };
};

export default useMagazineAuthorDetailViewModel;
